import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore'
import type { User } from 'firebase/auth'

export type LedgerStatus = 'ACTIVE' | 'PASSIVE' | 'DORMANT'

export type UserLedger = {
  email: string
  uid: string
  status: LedgerStatus
  personalCredits: number
  availableCredits?: number
  credit_balance?: number
  /**
   * Authoritative "first workspace ever" flag — the Experience Engine's
   * `initializeWorkspaceTrialWallet` Cloud Function sets this true the first
   * time the owner's first workspace is created. Legacy `hasReceivedPersonal/
   * TeamBonus` flags are honored so already-claimed users don't double-dip.
   */
  hasClaimedFirstWorkspaceBonus?: boolean
  /** @deprecated kept for back-compat with pre-Phase-4 ledgers. */
  hasReceivedPersonalBonus?: boolean
  /** @deprecated kept for back-compat with pre-Phase-4 ledgers. */
  hasReceivedTeamBonus?: boolean
  total_referrals?: number
  lastActiveAt: Timestamp
}

export const normalizeLedgerEmail = (email: string | null | undefined) => email?.trim().toLowerCase() ?? ''

export const getUserLedgerRef = (db: Firestore, email: string) =>
  doc(db, 'user_ledger', normalizeLedgerEmail(email))

export const getPersonalWalletRef = (db: Firestore, uid: string) =>
  doc(db, 'users', uid, 'wallet', 'personal')

export const getLedgerCreditBalance = (ledger: Partial<UserLedger> | undefined | null) => {
  if (typeof ledger?.availableCredits === 'number') return ledger.availableCredits
  if (typeof ledger?.credit_balance === 'number') return ledger.credit_balance
  if (typeof ledger?.personalCredits === 'number') return ledger.personalCredits
  return 0
}

const isPermissionDenied = (error: unknown) =>
  error instanceof Error && error.message.toLowerCase().includes('permission-denied')

export const seedUserLedger = async (
  db: Firestore,
  email: string,
  uid = '',
) => {
  const normalizedEmail = normalizeLedgerEmail(email)
  if (!normalizedEmail) return

  await runTransaction(db, async (transaction) => {
    transaction.set(
      getUserLedgerRef(db, normalizedEmail),
      {
        email: normalizedEmail,
        uid,
        status: 'ACTIVE',
        personalCredits: 250,
        credit_balance: 250,
        hasClaimedFirstWorkspaceBonus: false,
        hasReceivedPersonalBonus: false,
        hasReceivedTeamBonus: false,
        total_referrals: 0,
        lastActiveAt: serverTimestamp(),
      },
      { merge: true },
    )
  })
}

export const ensureUserLedger = async (
  db: Firestore,
  user: User,
): Promise<{ ledger: UserLedger | null; wasDormant: boolean }> => {
  const email = normalizeLedgerEmail(user.email)
  if (!email) return { ledger: null, wasDormant: false }

  const ledgerRef = getUserLedgerRef(db, email)

  try {
    return await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ledgerRef)

      if (!snapshot.exists()) {
        const nextLedger = {
          email,
          uid: user.uid,
          status: 'ACTIVE' as const,
          personalCredits: 200,
          credit_balance: 200,
          hasClaimedFirstWorkspaceBonus: false,
          hasReceivedPersonalBonus: false,
          hasReceivedTeamBonus: false,
          total_referrals: 0,
          lastActiveAt: serverTimestamp(),
        }

        transaction.set(ledgerRef, nextLedger)
        return { ledger: null, wasDormant: false }
      }

      const data = snapshot.data() as Partial<UserLedger>
      const wasDormant = data.status === 'DORMANT'

      transaction.set(
        ledgerRef,
        {
          email,
          uid: user.uid,
          status: 'ACTIVE',
          personalCredits: getLedgerCreditBalance(data),
          credit_balance: getLedgerCreditBalance(data),
          hasReceivedPersonalBonus: data.hasReceivedPersonalBonus === true,
          hasReceivedTeamBonus: data.hasReceivedTeamBonus === true,
          total_referrals: typeof data.total_referrals === 'number' ? data.total_referrals : 0,
          lastActiveAt: serverTimestamp(),
        },
        { merge: true },
      )

      return {
        ledger: data as UserLedger,
        wasDormant,
      }
    })
  } catch (error) {
    if (!isPermissionDenied(error)) throw error
    await seedUserLedger(db, email, user.uid)
    return { ledger: null, wasDormant: false }
  }
}

export const getUserLedger = async (db: Firestore, email: string) => {
  const normalizedEmail = normalizeLedgerEmail(email)
  if (!normalizedEmail) throw new Error('A valid account email is required.')

  // Clients cannot write to `user_ledger/{email}` — Firestore rules restrict
  // creation to the server. Attempt a best-effort seed for environments where
  // it IS permitted (emulator, admin SDK), but swallow permission-denied so
  // callers get a graceful `null` on brand-new accounts instead of an error.
  const trySeed = async () => {
    try {
      await seedUserLedger(db, normalizedEmail)
    } catch (error) {
      if (!isPermissionDenied(error)) throw error
    }
  }

  try {
    const snapshot = await getDoc(getUserLedgerRef(db, normalizedEmail))
    if (snapshot.exists()) return snapshot.data() as UserLedger
    await trySeed()
    return null
  } catch (error) {
    if (!isPermissionDenied(error)) throw error
    await trySeed()
    return null
  }
}

export const assertActiveLedger = async (db: Firestore, email: string) => {
  const ledger = await getUserLedger(db, email)

  if (!ledger) {
    throw new Error('No billing ledger exists for this account. Please sign out and sign in again.')
  }

  if (ledger.status === 'DORMANT') {
    throw new Error('This account is dormant. Please sign in again to reactivate it.')
  }

  return ledger
}
